import React from 'react';
import styled from 'styled-components';
import { TbCaretDownFilled } from 'react-icons/tb';
import { BoxProps } from 'core/components/types';
import { Box, Button, Collapse, Flex, Heading, Icon } from 'core/components';

const Caret = styled(Icon)`
  && {
    display: inline-flex;
    flex: 0 0 16px;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    transform-origin: center;
    transition: transform 0.2s ease-out;
    transform: ${(props) => (props.open ? 'rotate(180deg)' : 'rotate(0deg)')};
    color: rgba(0, 0, 0, 0.35);

    svg {
      display: block;
      width: 16px;
      height: 16px;
    }
  }
`;

const SectionWrapper = styled(Box)`
  position: relative;

  &.ude-measurement-header .section-header {
    background: #002766;
    border-bottom: none;
    margin-bottom: 12px;
  }

  &.ude-measurement-header.ude-query-header .section-header {
    margin-bottom: 0;
  }

  &.ude-measurement-header .section-header,
  &.ude-measurement-header .section-header .bp4-heading,
  &.ude-measurement-header .section-header .bp4-button,
  &.ude-measurement-header .section-header .bp4-button .bp4-icon {
    color: #fff;
  }

  &.ude-measurement-header .section-header .bp4-button .bp4-icon {
    color: rgba(255, 255, 255, 0.85);
  }

  &.ude-measurement-header .bp4-popover-wrapper,
  &.ude-measurement-header .bp4-popover-target {
    display: flex;
    align-items: center;
    min-width: 0;
    width: 100%;
  }

  .bp4-collapse {
    overflow: hidden !important;
  }

  .bp4-button.section-button {
    border-radius: 0;
  }

  .section-content {
    scrollbar-width: thin;
  }

  .section-caret {
    opacity: 0.35;
  }

  &:hover {
    .section-caret {
      opacity: 1;
    }
  }
`;

interface SidebarSectionProps extends BoxProps {
  title: React.ReactNode;
  children: React.ReactNode;
  icon?: React.ReactNode;
  iconProps?: React.ComponentProps<typeof Icon>;
  actions?: React.ReactNode;
  defaultIsOpen?: boolean;
  rightElement?: React.ReactNode;
  isCollapsible?: boolean;
  isOpen?: boolean;
  style?: React.CSSProperties;
}

const SidebarSection = ({
  title,
  children,
  icon,
  iconProps,
  actions,
  className,
  defaultIsOpen = false,
  rightElement,
  isCollapsible = true,
  isOpen,
  maxHeight,
  pl = 2,
  pr = 2,
  ...rest
}: SidebarSectionProps) => {
  const [internalOpen, setInternalOpen] = React.useState(defaultIsOpen);
  const isControlled = isOpen !== undefined;
  const currentOpen = isControlled ? isOpen : internalOpen;

  const toggle = () => {
    if (!isControlled && isCollapsible) {
      setInternalOpen(!internalOpen);
    }
  };

  const heading = (
    <Flex justifyContent="space-between" alignItems="center" width="100%" minHeight="20px">
      <Flex alignItems="center" gap={1} flex={1} minWidth={0}>
        {icon && <Icon icon={icon} size={16} color="kentik" {...iconProps} />}
        {typeof title === 'string' ? (
          <Heading level={6} m={0} flex={1} fontSize="13px" fontWeight="500">
            {title}
          </Heading>
        ) : (
          title
        )}
        {rightElement && rightElement}
      </Flex>
      <Flex gap="4px" alignItems="center">
        {actions}
        {isCollapsible && <Caret className="section-caret" open={currentOpen} icon={TbCaretDownFilled} />}
      </Flex>
    </Flex>
  );

  const body = (
    <Box className="section-content" position="relative" maxHeight={maxHeight}>
      {children}
    </Box>
  );

  return (
    <SectionWrapper className={className} borderBottom="thin" pb={currentOpen ? '12px' : 0} {...rest}>
      {isCollapsible && (
        <>
          <Button
            className="section-button"
            large
            fill
            minimal
            textAlign="left"
            onClick={toggle}
            minHeight="32px"
            pt="3px"
            pb="3px"
            pl="16px"
            mb={currentOpen ? '2px' : 0}
          >
            {heading}
          </Button>
          <Collapse isOpen={currentOpen}>{body}</Collapse>
        </>
      )}
      {!isCollapsible && (
        <>
          <Box className="section-header" pl={pl} pr={pr} py={1}>
            {heading}
          </Box>
          {body}
        </>
      )}
    </SectionWrapper>
  );
};

export default SidebarSection;
